

































































import {
  Component,
  Prop,
  Watch,
} from 'vue-property-decorator';
import { debounce } from 'lodash';
import FilterBuilder, { FilterBuilderInterface } from '@/classes/pimcore-filtering/FilterBuilder';
import { Filter } from '@/classes/pimcore-filtering/types';
import { like } from '@/classes/pimcore-filtering/Filters';
import EntityFilter from '@/components/Entity/EntityFilter.vue';
import {
  PimcoreLayoutInput, PimcoreLayoutPanel,
} from '@/types/entity';
import TypeInspector from '@/classes/graphql-introspection/TypeInspector';
import StorageMixin from '@/mixins/StorageMixin';

interface EntityFilterData {
  fieldName: string;
  value: Filter;
}

@Component({
  components: {
    EntityFilter,
  },
})
export default class EntityFilters extends StorageMixin {
  @Prop({
    type: Array,
    required: false,
    default: () => [],
  }) readonly only!: Array<string>

  @Prop({
    type: Object,
    default: () => ({}),
  }) readonly complexFiltersVariables!: Record<string, any>

  @Prop({
    type: Array,
    default: () => [],
  }) readonly extendedFilters!: Array<string>

  @Prop({
    type: String,
    default: () => '',
  }) readonly idsFilter!: string

  @Prop({
    type: Array,
    default: () => [],
  }) readonly queryFields!: Array<any>

  @Prop({
    type: Array,
    default: () => [],
  }) readonly gridHeadersLayoutFields!: Array<any>

  @Prop({
    type: Array,
    default: () => [],
  }) readonly gridHeaders!: Array<any>

  @Prop({
    type: String,
    required: false,
  }) readonly localStoragePrefix!: string

  loadedFilterCount = 0;

  initialized = false;

  searchValue = this.getFromStorage('search_value');

  fieldFilter = this.getFromStorage('field_filter');

  activeComplexFilters: Array<EntityFilterData> = [];

  created() {
    this.onSearch = debounce(this.onSearch, 500);
  }

  filterLoaded() {
    this.loadedFilterCount += 1;

    if (
      !this.initialized
      && this.loadedFilterCount > 0
      && this.loadedFilterCount === this.complexFilters.length
    ) {
      this.initialized = true;
      this.onSearch();
    }
  }

  onSearch() {
    if (this.fieldFilter) {
      this.setToStorage('field_filter', this.fieldFilter);
      this.setToStorage('search_value', this.searchValue);
    }
    this.$emit('onSearch', this.getFilterBuilder());
  }

  setFilter(fieldName: string, filter: Filter) {
    const filterIndex = this.activeComplexFilters.findIndex((v: EntityFilterData) => v.fieldName === fieldName);
    if (filterIndex !== -1) {
      this.activeComplexFilters.splice(filterIndex, 1);
    }

    if (filter) {
      this.activeComplexFilters.push({
        fieldName,
        value: filter,
      });
    }
  }

  checkFilterAccess(field: PimcoreLayoutPanel|PimcoreLayoutInput) {
    const headersFields = this.gridHeadersLayoutFields?.length ? this.gridHeadersLayoutFields : this.gridHeaders;
    return headersFields.some((f: any) => {
      const onlyCheck = !this.only?.length || this.only.includes(f.name);
      return f.name === field.name && onlyCheck;
    });
  }

  get plainFilters() {
    return this.queryFields.filter((field: PimcoreLayoutPanel|PimcoreLayoutInput) => {
      const isPlain = TypeInspector.isScalarType(field.type);
      const inGrid = this.checkFilterAccess(field);
      const isExtended = this.extendedFilters.length ? this.extendedFilters.some((filter) => filter === field.name) : false;
      return isPlain && inGrid && !isExtended;
    }).map((field: PimcoreLayoutPanel|PimcoreLayoutInput) => ({
      text: this.$t(field.name),
      value: field.name,
    }));
  }

  get complexFilters() {
    return this.queryFields.filter((field: PimcoreLayoutPanel|PimcoreLayoutInput) => {
      const isComplex = TypeInspector.isObjectType(field.type) || TypeInspector.isUnionType(field.type);
      const inGrid = this.checkFilterAccess(field);
      const isExtended = this.extendedFilters.length ? this.extendedFilters.some((filter) => filter === field.name) : false;
      return isComplex && inGrid && !isExtended;
    }).map((field: PimcoreLayoutPanel|PimcoreLayoutInput) => {
      const types = field.type.getTypes();
      if (!types.length || !types[0].name) {
        return null;
      }
      return {
        // getting entity full name by removing object_ from the beginning of name
        fieldName: field.name,
        entity: types[0].name.slice(7),
        variables: this.complexFiltersVariables[field.name] || {},
      };
    });
  }

  get pimcoreFieldFilter() {
    return like(`${this.fieldFilter}`, `%${this.searchValue || ''}%`);
  }

  getFilterBuilder() {
    const filterBuilder: FilterBuilderInterface = new FilterBuilder();
    if (this.searchValue) filterBuilder.addFilters(this.pimcoreFieldFilter);
    for (let i = 0; i < this.activeComplexFilters.length; i += 1) {
      const filter = this.activeComplexFilters[i];
      if (filter.value) {
        filterBuilder.addFilters(filter.value);
      }
    }
    return filterBuilder;
  }

  clearFilters() {
    this.activeComplexFilters = [];
    this.searchValue = '';
    this.onSearch();
    this.$bus.$emit('clear-filters');
  }

  @Watch('plainFilters')
  plainFiltersChanged(plainFilters: any) {
    this.fieldFilter = this.getFromStorage('field_filter') || plainFilters[0]?.value;
  }
}

